import * as React from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * The one button.
 *
 * Appearance lives in `styles/ui.css` under `.btn`. This file owns only the
 * behaviour: the loading swap, the disabled coupling, and the variant/size props
 * that resolve to exactly one modifier class each - so there is never more than
 * one class per axis and never a conflicting-utility problem to solve.
 *
 * Sizes are two scales on one axis. `sm` (32px), `md` (36px, the default) and
 * `lg` (44px) are the console, where the input is a mouse. `touch` (56px) and
 * `field` (72px) are the glove-operable floors from PRD §11, for a screen a
 * technician uses outdoors.
 *
 * They are named rather than continuing the t-shirt scale on purpose: `xl` gave
 * no reason for its size, so console pages reached for it whenever a button
 * looked lost, and the console ended up built to a phone's proportions.
 *
 * `panel` and `panel-ghost` exist because the deep chrome surface is a different
 * colour problem, not a different component: `secondary` on a white card is a
 * bordered white button, and the same button on near-black has to be a
 * translucent white overlay or it punches a hole in the panel.
 */

type Variant = "primary" | "secondary" | "ghost" | "danger" | "panel" | "panel-ghost";
type Size = "sm" | "md" | "lg" | "touch" | "field";

export interface ButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "className"
> {
  variant?: Variant;
  size?: Size;
  /** Swaps the label for a spinner and disables the button. */
  loading?: boolean;
  /** Stretch to the container. The default on mobile-width auth forms. */
  block?: boolean;
  icon?: React.ReactNode;
  /** Rendered after the label - for the arrow on a call to action. */
  trailingIcon?: React.ReactNode;
  className?: string;
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  block = false,
  icon,
  trailingIcon,
  disabled,
  children,
  className,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      // aria-busy rather than swapping the accessible name: a screen reader
      // should not hear the button's label disappear mid-submit.
      aria-busy={loading || undefined}
      className={cn("btn", `btn--${variant}`, `btn--${size}`, block && "btn--block", className)}
      {...rest}
    >
      {loading ? <Loader2 aria-hidden className="btn__spinner" /> : icon}
      {children}
      {trailingIcon}
    </button>
  );
}

/**
 * The trailing arrow on a call to action, which slides a hair on hover.
 *
 * Lives here because it depends on `Button`'s internals: the nudge is driven by
 * `group-hover/btn`, and that named group only exists on the button itself. Left
 * at the call site it silently does nothing the moment somebody uses it outside
 * a `Button` - a dead animation with no error to notice.
 */
export function NudgeArrow() {
  return <ArrowRight className="btn__nudge" />;
}
